import { useRef as useReactRef } from "react";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { ColumnDef } from "@tanstack/react-table";
import { useSocket } from "@/contexts/SocketContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import type { ImpressaoItem } from "@/types/impressao-item";
import { alert } from "@/contexts/AlertModalContext";
import {
  serializarTratativas,
  tratativaAtiva,
} from "@/lib/tratativasImpressao";
import {
  AlertTriangle,
  Check,
  Clock,
  Edit3,
  Keyboard,
  Loader2,
  Printer,
  RotateCw,
  Trash2,
  X,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { downloadImpressaoLegacy } from "@/utils/UtilFuncoes";
import ZebraBrowserPrintWrapper from "zebra-browser-print-wrapper";
import { useItem } from "@/contexts/ItemContext";
import type { newHistoricoItem } from "@/types/new-historico-item";
import {
  MODOS_IMPRESSAO,
  resolverCanalImpressao,
} from "@/lib/modosImpressao";
import { useHistorico } from "@/contexts/HistoricoContext";
import type { historicoItemResponse } from "@/types/historico-item-response-";
import { useGlobalLoader } from "../GlobalLoader";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { DataTable } from "../data-table";
import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import { useEpc } from "@/contexts/EpcContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useVerificarImpressao } from "@/contexts/VerificarImpressaoContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import {
  dividirDownloadGraficoEmPacotes,
  normalizarDownloadsGraficos,
} from "@/utils/zplDesigner";

type ImpressaoPanvelContextType = {
  adicionarItensNaFila: (
    categoriaSelecionada: CategoriaItemResponse,
    localizacaoSelecionada: string,
    localizacaoSelecionada2: string,
    localizacaoSelecionada3: string,
    localizacaoSelecionada4: string,
  ) => void;
  fila: ImpressaoItem[];
  estaImprimindo: boolean;
  tagsImpressasNum: React.MutableRefObject<number>;
  finalizarImpressao: (sucesso?: boolean) => void;
  /** Descarta o lote que ainda não foi para a base (modo "salvar no final"). */
  descartarSalvamentoPendente: () => void;
  deuErro: boolean;
  erroMensagem: string;
  cardConfirmarImpressao: boolean;
  setCardConfirmarImpressao: (value: boolean) => void;
  handleAlterarItens: () => void;
  cardAlterarItens: boolean;
  setFila: React.Dispatch<React.SetStateAction<ImpressaoItem[]>>;
  setItensParaRemocao: React.Dispatch<React.SetStateAction<ImpressaoItem[]>>;
  itensParaRemocao: ImpressaoItem[];
  codigoDigitado: string;
  travarSeisDígitos: boolean;
  setTravarSeisDígitos: React.Dispatch<React.SetStateAction<boolean>>;
};

const ImpressaoPanvelContext = createContext<
  ImpressaoPanvelContextType | undefined
>(undefined);

export function ImpressaoPanvelProvider({ children }: { children: ReactNode }) {
  const { numeroDeImpressoes } = useContaConfig();
  const [erroVisual, setErroVisual] = useState(false);
  const { localizacoes } = useLocalizacoes();
  const ERRO_AUDIO_PATH = "/error.mp3";
  const erroAudioRef = useReactRef<HTMLAudioElement>(null);
  const { gerarEpcs } = useEpc();
  const { setIsLoading } = useGlobalLoader();
  const { impressaoConfig } = useImpressaoConfig();
  const [estaImprimindo, setEstaImprimindo] = useState(false);
  const [fila, setFila] = useState<ImpressaoItem[]>([]);
  const { isConnected, lastMessage, enviarMensagem } = useSocket();
  const [impressaoPausada, setImpressaoPausada] = useState(false);
  const indiceAtual = useRef(0);
  // Último índice da fila já enviado ao firmware. O efeito de impressão
  // re-dispara em qualquer mudança de identidade das dependências (ex.:
  // atualizarEtiquetasImpressas recria impressaoConfig durante o salvarNaBase,
  // antes de indiceAtual avançar) — sem esta guarda o item recém-impresso era
  // reenviado e saía em duplicata. Retentativas manuais/automáticas chamam
  // imprimirItem direto e não passam por esta guarda.
  const ultimoIndiceEnviado = useRef(-1);
  const tentativas = useRef(0);
  const removeuLogo = useRef(false);
  // BLALL: varredura de tags virgens "soltas" sob a antena antes da ordem.
  // Enviado uma vez por ordem; aguardamos a resposta (firmware >= 2.07) antes
  // de iniciar a fila. Firmwares antigos nao respondem -- caimos no timeout
  // e seguimos a impressao normalmente.
  const blallEnviado = useRef(false);
  const blallExecutado = useRef(false);
  const blallTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const BLALL_TIMEOUT_MS = 2500;
  // CLRQ: limpa a fila de impressao do firmware no inicio de cada ordem.
  // Descarta defeitos (ou outros itens) orfaos de ordens anteriores que
  // ainda nao foram drenados. Fire-and-forget: firmwares < 2.07 ignoram
  // o comando desconhecido. Suportado a partir do firmware 2.07.
  const clrqEnviado = useRef(false);
  const [deuErro, setDeuErro] = useState(false);
  const [erroMensagem, setErroMensagem] = useState("");
  const [cardConfirmarImpressao, setCardConfirmarImpressao] = useState(false);
  const [cardAlterarItens, setCardAlterarItens] = useState(false);
  const { salvarItensNaBase, pesquisarItensNaBase } = useItem();
  const { coletarBlocosEPC } = useImpressaoConfig();
  const [ultimoCodigoProcessado, setUltimoCodigoProcessado] = useState("");
  const [erros, setErros] = useState<string[]>([]);
  const { verificarImpressao } = useVerificarImpressao();
  const { contaConfig } = useContaConfig();

  const { criarHistorico, atualizarHistorico } = useHistorico();
  const [criouHistorico, setCriouHistorico] = useState(false);
  const [itensParaRemocao, setItensParaRemocao] = useState<ImpressaoItem[]>([]);
  const [historicoCriado, setHistoricoCriado] = useState<
    historicoItemResponse | undefined
  >();
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<
    CategoriaItemResponse | undefined
  >(undefined);
  const [localizacaoSelecionada, setLocalizacaoSelecionada] =
    useState<string>("");
  const [localizacaoSelecionada2, setLocalizacaoSelecionada2] =
    useState<string>("");
  const [localizacaoSelecionada3, setLocalizacaoSelecionada3] =
    useState<string>("");
  const [localizacaoSelecionada4, setLocalizacaoSelecionada4] =
    useState<string>("");
  const tagsImpressasNum = useRef(0);
  // Itens já impressos que ainda não foram para a base. Só enche quando a conta
  // escolhe salvar no fim da impressão.
  const itensPendentesRef = useRef<ImpressaoItem[]>([]);

  const [codigoDigitado, setCodigoDigitado] = useState("");
  const [travarSeisDígitos, setTravarSeisDígitos] = useState(() => {
    const savedValue = localStorage.getItem(
      "impressaoPanvel_travarSeisDígitos",
    );
    return savedValue !== null ? JSON.parse(savedValue) : true; // Default true se não existir
  });

  const handleAtivarCardConfirmarImpressao = async () => {
    await descarregarSalvamentoPendente();
    await verificarImpressao(fila.slice(0, tagsImpressasNum.current));
    setCardConfirmarImpressao(true);
  };

  const regravarEtiqueta = () => {
    if (impressaoPausada) return;
    if (indiceAtual.current < fila.length) {
      const itemAtual = fila[indiceAtual.current];
      if (itemAtual.zpl_impressao) {
        let zpl_vazio = "^XA^XZ";
        let itemAtualSemZpl: ImpressaoItem = {
          ...itemAtual,
        };
        itemAtualSemZpl.zpl_impressao = zpl_vazio;
        imprimirItem(itemAtualSemZpl);
      } else {
        alert.error("Etiqueta não possui ZPL para regravar.");
      }
    } else {
      alert.error("Nenhum item na fila para regravar.");
    }
  };

  // Salvar no localStorage sempre que travarSeisDígitos mudar
  useEffect(() => {
    localStorage.setItem(
      "impressaoPanvel_travarSeisDígitos",
      JSON.stringify(travarSeisDígitos),
    );
  }, [travarSeisDígitos]);

  const processarCodigoDigitado = (codigo: string) => {
    if (codigo.length > 0 && codigo === ultimoCodigoProcessado) {
      setCodigoDigitado("");
      return; // Ignora código repetido
    }
    if (deuErro) {
      setErroVisual(true);
      if (erroAudioRef.current) {
        erroAudioRef.current.currentTime = 0;
        erroAudioRef.current.play();
      }
      setTimeout(() => setErroVisual(false), 3000);
      alert.error("Impressão bloqueada devido a erro.");
      setCodigoDigitado("");
      return;
    }
    if (!codigo) {
      alert.error("Código digitado não pode ser vazio.");
      return;
    }
    if (travarSeisDígitos && codigo.length !== 6) {
      setErroVisual(true);
      if (erroAudioRef.current) {
        erroAudioRef.current.currentTime = 0;
        erroAudioRef.current.play();
      }
      setTimeout(() => setErroVisual(false), 3000);
      alert.error("O código digitado deve ter exatamente 6 dígitos.");
      setCodigoDigitado("");
      return;
    }
    // Só permite processar novo código se todos os itens da fila estiverem com estado 'Impresso'
    const todosImpressos =
      fila.length === 0 ||
      fila.every((_, idx) => idx < tagsImpressasNum.current);
    if (!todosImpressos) {
      alert.info(
        "Aguarde a impressão de todos os itens antes de adicionar outro código.",
      );
      // Não limpa o campo, mantém o códigoDigitado
      return;
    }
    if (categoriaSelecionada && impressaoConfig) {
      const localizacoesZpl: string[] = [
        localizacoes.find((loc) => loc._id === localizacaoSelecionada)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((loc) => loc._id === localizacaoSelecionada2)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((n2) => n2._id === localizacaoSelecionada2)
          ?.children.find((loc) => loc._id === localizacaoSelecionada3)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((n2) => n2._id === localizacaoSelecionada2)
          ?.children.find((n3) => n3._id === localizacaoSelecionada3)
          ?.children.find((loc) => loc._id === localizacaoSelecionada4)
          ?.descricao || "",
      ];
      const epc = gerarEpcs(
        categoriaSelecionada,
        1,
        localizacoesZpl,
        coletarBlocosEPC(),
        undefined,
        codigoDigitado,
      );
      const epcGerado = epc[0].epc;
      // Verifica se já existe esse EPC na fila
      if (fila.some((item) => item.tag === epcGerado)) {
        setErroVisual(true);
        if (erroAudioRef.current) {
          erroAudioRef.current.currentTime = 0;
          erroAudioRef.current.play();
        }
        setTimeout(() => setErroVisual(false), 3000);
        alert.error("Este EPC já está na fila para impressão.");
        setCodigoDigitado("");
        return;
      }
      // Verifica se já existe esse EPC na base
      pesquisarItensNaBase([epcGerado]).then((result) => {
        if (result && result.length > 0) {
          setErroVisual(true);
          if (erroAudioRef.current) {
            erroAudioRef.current.currentTime = 0;
            erroAudioRef.current.play();
          }
          setTimeout(() => setErroVisual(false), 3000);
          salvarNaBase([], `${epcGerado}: EPC JA ESTA CADASTRADO NA BASE`);
          alert.error("Este EPC já está cadastrado na base.");
          setCodigoDigitado("");
          return;
        }
        const item: ImpressaoItem = {
          codigo: categoriaSelecionada.ean,
          descricao: categoriaSelecionada.descricao,
          dt_validade: "",
          id_categoria: categoriaSelecionada._id,
          id_conta: impressaoConfig?.id_conta,
          id_localizacao1: localizacaoSelecionada,
          id_localizacao2: localizacaoSelecionada2,
          id_localizacao3: localizacaoSelecionada3,
          id_localizacao4: localizacaoSelecionada4,
          id_usuario: impressaoConfig?.id_usuario,
          inf_compl_1: categoriaSelecionada.inf_compl_1,
          inf_compl_2: categoriaSelecionada.inf_compl_2,
          inf_compl_3: categoriaSelecionada.inf_compl_3,
          inf_compl_4: categoriaSelecionada.inf_compl_4,
          inf_compl_5: categoriaSelecionada.inf_compl_5,
          inf_compl_6: categoriaSelecionada.inf_compl_6,
          inf_compl_7: categoriaSelecionada.inf_compl_7,
          inf_compl_8: categoriaSelecionada.inf_compl_8,
          inf_compl_9: categoriaSelecionada.inf_compl_9,
          inf_compl_10: categoriaSelecionada.inf_compl_10,
          inf_compl_11: categoriaSelecionada.inf_compl_11,
          inf_compl_12: categoriaSelecionada.inf_compl_12,
          quantidade: "",
          tag: epcGerado,
          valor: 0,
          zpl_impressao: epc[0].zpl_impressao_bo,
          _id: epc[0]._id,
          n_impressao: numeroDeImpressoes.toString(),
          id_externo: "",
          ultima_localizacao: {
            verificar: contaConfig?.transferencia_loc_origem || false,
            primeira_vez: true,
          },
        };
        setFila((prev) => [...prev, item]);
        setUltimoCodigoProcessado(codigo);
        setCodigoDigitado("");
      });
    }
  };
  // Quando todos os itens da fila estiverem impressos, processa automaticamente o código digitado se houver
  useEffect(() => {
    const todosImpressos =
      fila.length === 0 ||
      fila.every((_, idx) => idx < tagsImpressasNum.current);
    if (
      todosImpressos &&
      codigoDigitado.length > 0 &&
      (!travarSeisDígitos || codigoDigitado.length === 6)
    ) {
      processarCodigoDigitado(codigoDigitado);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fila.length, tagsImpressasNum.current]);

  const [lastEnterPress, setLastEnterPress] = useState(0);
  const DEBOUNCE_DELAY = 1000; // 1 second delay between Enter presses

  useEffect(() => {
    if (!estaImprimindo) return;
    if (
      codigoDigitado.length > 0 &&
      codigoDigitado === ultimoCodigoProcessado
    ) {
      setCodigoDigitado("");
      return; // Ignora código repetido
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const now = Date.now();
        // Check if enough time has passed since last Enter press
        if (now - lastEnterPress < DEBOUNCE_DELAY) {
          event.preventDefault();
          return; // Ignore rapid Enter presses
        }
        setLastEnterPress(now);

        if (codigoDigitado.trim()) {
          processarCodigoDigitado(codigoDigitado.trim());
        }
      } else if (event.key === "Backspace") {
        setCodigoDigitado((prev) => prev.slice(0, -1));
      } else if (event.key.length === 1) {
        // Apenas caracteres hexadecimais: A-F e 0-9
        const isHexCharacter = /^[0-9A-Fa-f]$/.test(event.key);
        if (isHexCharacter) {
          setCodigoDigitado((prev) => prev + event.key.toUpperCase());
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [estaImprimindo, codigoDigitado, ultimoCodigoProcessado, lastEnterPress]);

  const handleAlterarItens = () => {
    setCardAlterarItens(true);
    setCardConfirmarImpressao(false);
  };

  const adicionarItensNaFila = async (
    _categoriaSelecionada: CategoriaItemResponse,
    _localizacaoSelecionada: string,
    _localizacaoSelecionada2: string,
    _localizacaoSelecionada3: string,
    _localizacaoSelecionada4: string,
  ) => {
    try {
      if (!criouHistorico) {
        const newHistorico: newHistoricoItem = {
          campo_descricao_ordem_impressao:
            impressaoConfig?.campo_descricao_ordem_impressao || "-",
          etiqueta_padrao: impressaoConfig?.etiqueta_padrao || "-",
          hardware_id: impressaoConfig?.app_id || "-",
          id_conta: impressaoConfig?.id_conta || "-",
          id_usuario: impressaoConfig?.id_usuario || "-",
          nome_da_ordem: `impressaoPanvel-${new Date().toISOString()}-${Math.random()
            .toString(36)
            .substring(2, 15)}-${impressaoConfig?.id_conta}`,
          ordem_itens_tratados: [],
          padrao_epc: impressaoConfig?.padrao_epc || "",
          posicao_gtin_impressao:
            impressaoConfig?.posicao_gtin_impressao || "-",
          potencia_padrao: impressaoConfig?.potencia_padrao || "2",
          estado_da_ordem: "Finalizado",
          observacao: (() => {
            const local = [
              localizacoes.find((l) => l._id === _localizacaoSelecionada)
                ?.descricao,
              localizacoes
                .flatMap((l) => l.children)
                .find((l2) => l2._id === _localizacaoSelecionada2)?.descricao,
              localizacoes
                .flatMap((l) => l.children.flatMap((l2) => l2.children))
                .find((l3) => l3._id === _localizacaoSelecionada3)?.descricao,
              localizacoes
                .flatMap((l) => l.children.flatMap((l2) => l2.children))
                .flatMap((l3) => l3.children)
                .find((l4) => l4._id === _localizacaoSelecionada4)?.descricao,
            ]
              .filter(Boolean)
              .join(" > ");
            const linhas: string[] = [MODOS_IMPRESSAO.BIPAGEM];
            if (_categoriaSelecionada?.descricao)
              linhas.push(`Categoria: ${_categoriaSelecionada.descricao}`);
            if (local) linhas.push(`Local: ${local}`);
            return linhas.join("\n");
          })(),
          modo_impressao: MODOS_IMPRESSAO.BIPAGEM,
          detalhe_modo_impressao: _categoriaSelecionada?.descricao || "",
          canal_impressao: resolverCanalImpressao(impressaoConfig),
        };
        setCriouHistorico(true);
        const novoHistoricoCriado = await criarHistorico(newHistorico);
        setHistoricoCriado(novoHistoricoCriado);
      }
    } catch (error) {
      finalizarImpressao(false);
      console.log(error);
      return;
    }
    setLocalizacaoSelecionada(_localizacaoSelecionada);
    setLocalizacaoSelecionada2(_localizacaoSelecionada2);
    setLocalizacaoSelecionada3(_localizacaoSelecionada3);
    setLocalizacaoSelecionada4(_localizacaoSelecionada4);
    setEstaImprimindo(true);
    setCategoriaSelecionada(_categoriaSelecionada);
  };

  useEffect(() => {
    if (deuErro) return; // Bloqueia impressão após erro
    if (estaImprimindo && fila.length > 0 && isConnected) {
      if (!clrqEnviado.current) {
        clrqEnviado.current = true;
        enviarMensagem("CLRQ");
      }
      const ehSocketRfid =
        impressaoConfig?.impressao_usb !== "true" &&
        impressaoConfig?.impressao_legacy !== "true" &&
        !impressaoConfig?.gravacao_reversa;
      if (ehSocketRfid && !blallExecutado.current) {
        if (!blallEnviado.current) {
          blallEnviado.current = true;
          enviarMensagem("BLALL");
          if (!impressaoConfig?.apenas_gravar) {
            // alert.success vai pra toast auto-dismiss (bottom-right);
            // alert.info abriria modal bloqueante.
            alert.success(
              "Verificando etiquetas no campo antes de iniciar a ordem...",
              { title: "Aguarde" },
            );
          }
          // Firmwares < 2.07 nao respondem ao BLALL. Sem este fallback a
          // fila ficaria parada esperando pra sempre.
          blallTimeoutId.current = setTimeout(() => {
            if (blallExecutado.current) return;
            blallExecutado.current = true;
            blallTimeoutId.current = null;
            if (fila.length > 0 && isConnected && estaImprimindo) {
              imprimirItem(fila[indiceAtual.current]);
            }
          }, BLALL_TIMEOUT_MS);
        }
        return;
      }
      if (indiceAtual.current >= fila.length) return;
      if (ultimoIndiceEnviado.current === indiceAtual.current) return;
      imprimirItem(fila[indiceAtual.current]);
    }
  }, [fila, estaImprimindo, isConnected, impressaoConfig, deuErro]);

  const marcarDefeito = async (defeito: string) => {
    if (impressaoConfig?.zpl_marcacao_defeito) {
      let zplMarcacaoDefeito = impressaoConfig.zpl_marcacao_defeito;
      zplMarcacaoDefeito = zplMarcacaoDefeito.replace(
        "{erro}",
        defeito || "N/A",
      );
      enviarMensagem(formarPacoteParaEnvio(zplMarcacaoDefeito, true));
    }
  };

  useEffect(() => {
    if (!lastMessage) return;
    if (!estaImprimindo) return;

    const processarMensagem = async () => {
      const resposta = lastMessage.data.toLowerCase();

      // Resposta do BLALL: libera o início da fila independentemente de
      // OK/BUSY/NOMOD — o firmware ja faz a varredura ou explica por que nao.
      if (resposta.startsWith("blall|")) {
        // Guarda contra double-fire: se o timeout (ou uma resposta anterior)
        // ja iniciou a impressao, ignora resposta tardia/duplicada.
        if (blallExecutado.current) return;
        blallExecutado.current = true;
        if (blallTimeoutId.current) {
          clearTimeout(blallTimeoutId.current);
          blallTimeoutId.current = null;
        }
        if (fila.length > 0 && isConnected && estaImprimindo) {
          await imprimirItem(fila[indiceAtual.current]);
        }
        return;
      }

      const itemAtual = fila[indiceAtual.current];

      if (resposta.includes("no printer")) {
        salvarNaBase([], "Impressora não conectada ou indisponível.");
        alert.error("Impressora não conectada ou indisponível.");
        return;
      }

      if (resposta.includes("rlea")) {
        let epcLido = resposta.split("+")[1];
        if (!epcLido) {
          salvarNaBase([], "Etiqueta não encontrada ou inválida.");
          alert.error("Etiqueta não encontrada ou inválida.");
          return;
        }
        epcLido = epcLido.trim();
        alert.info(`Etiqueta lida: ${epcLido}`);
        return;
      }

      if (resposta.includes("continuar")) {
        if (impressaoPausada) {
          return;
        }
        indiceAtual.current++;
        return;
      }

      if (impressaoConfig?.gravacao_reversa) {
        if (resposta.includes("grrv-")) {
          const epc: string = resposta
            .replace("grrv-", "")
            .trim()
            .toUpperCase();
          const epcJaUsado = fila.some(
            (item) => item.tag.toUpperCase() === epc.toUpperCase(),
          );
          const maxTentativas =
            impressaoConfig?.tratativa_automatica_tentativas_ordem ?? 0;
          if (epcJaUsado) {
            if (maxTentativas > 0 && tentativas.current < maxTentativas) {
              alert.warning(`EPC ${epc} já usado. Tentando regravar...`);
              tentativas.current++;
              marcarDefeito("EPC JA ESTA EM USO");
              salvarNaBase([], `${epc}: EPC JA ESTA EM USO`);

              regravarEtiqueta();
              return;
            } else {
              setDeuErro(true);
              setErroVisual(true);
              if (erroAudioRef.current) {
                erroAudioRef.current.currentTime = 0;
                erroAudioRef.current.play();
              }
              setTimeout(() => setErroVisual(false), 3000);
              let erroMsg = `EPC ${epc} já está em uso.`;
              salvarNaBase([], erroMsg);
              alert.error(erroMsg);
              setErroMensagem(erroMsg);
              return;
            }
          }
          fila[indiceAtual.current].tag = epc.toUpperCase();
          enviarMensagem("BL+" + epc.toUpperCase());

          await salvarNaBase([fila[indiceAtual.current]]);
          indiceAtual.current++;
          tagsImpressasNum.current++;
          return;
        }
      }

      if (resposta.includes("imprimiu") || resposta.includes("gravou")) {
        enviarMensagem("BL+" + itemAtual.tag.toUpperCase());
        await salvarNaBase([itemAtual]);
        indiceAtual.current++;
        tentativas.current = 0;
        tagsImpressasNum.current++;
      } else if (resposta.includes("erro")) {
        const ehNenhumaTagEncontrada = resposta.includes(
          "erro gravar nenhuma tag encontrada",
        );
        const maxTentativas =
          impressaoConfig?.tratativa_automatica_tentativas_ordem ?? 0;
        if (maxTentativas > 0 && tentativas.current < maxTentativas) {
          tentativas.current++;
          const motivoDefeito = ehNenhumaTagEncontrada
            ? "NENHUMA TAG VIRGEM ENCONTRADA"
            : "ERRO AO GRAVAR";
          marcarDefeito(motivoDefeito);
          salvarNaBase(
            [],
            ehNenhumaTagEncontrada
              ? `${itemAtual.tag}: NENHUMA TAG VIRGEM ENCONTRADA`
              : `${itemAtual.tag}: EPC JA ESTA EM USO`,
          );
          await imprimirItem(itemAtual);
        } else {
          setDeuErro(true);
          setErroVisual(true);
          if (erroAudioRef.current) {
            erroAudioRef.current.currentTime = 0;
            erroAudioRef.current.play();
          }
          setTimeout(() => setErroVisual(false), 3000);
          const sufixoTentativas =
            maxTentativas > 0
              ? ` após ${maxTentativas} tentativa${maxTentativas > 1 ? "s" : ""}`
              : "";
          let erroMsg = resposta;
          if (ehNenhumaTagEncontrada) {
            erroMsg = `Nenhuma etiqueta encontrada para gravação${sufixoTentativas}.`;
            if (!impressaoConfig?.apenas_gravar) {
              salvarNaBase([], `${itemAtual.tag}: ${erroMsg}`);
              alert.error(erroMsg);
            }
          } else if (
            resposta.includes("erro gravar nao foi possivel gravar a etiqueta")
          ) {
            erroMsg = `Erro ao gravar etiqueta ${itemAtual.tag}${sufixoTentativas}.`;
            salvarNaBase([], `${itemAtual.tag}: Erro ao gravar etiqueta`);

            alert.error(erroMsg);
          } else {
            erroMsg = `Erro desconhecido${sufixoTentativas}: ${resposta}`;
            salvarNaBase(
              [],
              `${itemAtual.tag}: Erro desconhecido: ${resposta}`,
            );
            alert.error(erroMsg);
          }
          setErroMensagem(erroMsg);
        }
      }
    };

    processarMensagem();
  }, [lastMessage]);

  const formarPacoteParaEnvio = (
    pacote: string,
    defeito: boolean = false,
    epcOverride?: string,
  ) => {
    let delay = impressaoConfig?.tempo_delay_gravacao || "720";
    let tentativasGravacao = impressaoConfig?.tentativas_gravacao || "1";
    let forcaPadrao = impressaoConfig?.potencia_padrao || "2";
    let inicioTagVirgem = impressaoConfig?.inicio_tag_virgem || "E280";
    let priorizarMaiorEtiqueta = impressaoConfig?.tag_virgem_maior_epc
      ? "true"
      : "false";
    let socketPuro = "false";
    let idSocketPuro = "-";
    let apenasGravar = "false";
    let travarEtiqueta = "false";
    let primeiraDaOrdem = "false";
    let conferirEtiquetaAposGravacao = tratativaAtiva(
      impressaoConfig,
      "conferir_tag_apos_gravar",
    )
      ? "true"
      : "false";
    // Campo extra (sempre no fim do pacote): estado das tratativas de
    // gravação. Firmware antigo ignora o token; firmware 2.26+ usa.
    let tratativas = serializarTratativas(impressaoConfig);
    let temRfid = "false";

    let pacoteFormatado = "";
    if (defeito == true) {
      pacoteFormatado = `-${pacote}|${temRfid}|FFFFFFFFFFFFFFFFFFFFFFFF|${delay}|${apenasGravar}|${socketPuro}|${idSocketPuro}|${travarEtiqueta}|${tentativasGravacao}|${primeiraDaOrdem}|${inicioTagVirgem}|${priorizarMaiorEtiqueta}|${forcaPadrao}|${conferirEtiquetaAposGravacao}|${tratativas}`;
    } else if (impressaoConfig?.gravacao_reversa) {
      pacoteFormatado = `*${pacote}|${delay}|${tentativasGravacao}|${inicioTagVirgem}|${priorizarMaiorEtiqueta}|${forcaPadrao}|${socketPuro}|${idSocketPuro}`;
    } else {
      let epc = epcOverride ?? fila[indiceAtual.current].tag ?? "";
      pacoteFormatado = `+${pacote}|${temRfid}|${epc}|${delay}|${apenasGravar}|${socketPuro}|${idSocketPuro}|${travarEtiqueta}|${tentativasGravacao}|${primeiraDaOrdem}|${inicioTagVirgem}|${priorizarMaiorEtiqueta}|${forcaPadrao}|${conferirEtiquetaAposGravacao}|${tratativas}`;
    }

    return pacoteFormatado;
  };

  const imprimirItem = async (item: ImpressaoItem) => {
    if (!item) return;
    ultimoIndiceEnviado.current = indiceAtual.current;
    setDeuErro(false);
    // ~DG escrito dentro do ^XA nao grava na impressora; sobe pra fora antes
    const zplNormalizado = normalizarDownloadsGraficos(item?.zpl_impressao);
    const logo = zplNormalizado.split("^XA")[0];
    const zplSemLogo = "^XA" + zplNormalizado.split("^XA")[1] || "";

    const zplLimpo = zplSemLogo
      .replace(/\^XA/g, "")
      .replace(/\^XZ/g, "")
      .trim();

    if (impressaoConfig?.impressao_usb == "true") {
      try {
        if (impressaoConfig?.gravacao_reversa) {
          alert.error("Impressão USB não é compatível com gravação reversa.");
          finalizarImpressao(false);
          return;
        }
        const browserPrint = new ZebraBrowserPrintWrapper();
        const defaultPrinter = await browserPrint.getDefaultPrinter();
        browserPrint.setPrinter(defaultPrinter);
        const printerStatus = await browserPrint.checkPrinterStatus();
        if (printerStatus.isReadyToPrint) {
          if (!removeuLogo.current && logo.length) {
            browserPrint.print(logo);
            removeuLogo.current = true;
          }
          for (let i = 0; i < fila.length; i++) {
            let zplSemLogoUsb =
              normalizarDownloadsGraficos(fila[i].zpl_impressao).split(
                "^XA",
              )[1] || "";
            zplSemLogoUsb = zplSemLogoUsb.replace(/\^XZ/g, "").trim();
            await browserPrint.print(`^XA${zplSemLogoUsb}^XZ`);
            indiceAtual.current++;
            tagsImpressasNum.current++;
            handleAtivarCardConfirmarImpressao();
          }
          return true;
        } else {
          throw new Error(
            `Erro ao imprimir via USB: Impressão não está pronta.`,
          );
        }
      } catch (error) {
        if (error == "Error: TypeError: Failed to fetch") {
          alert.error(
            "Erro ao conectar com a impressora USB. Verifique a conexão.",
          );
        } else {
          alert.error("Erro ao imprimir via USB: " + error);
        }

        finalizarImpressao(false);
        return;
      }
    }
    if (impressaoConfig?.impressao_legacy == "true") {
      if (impressaoConfig?.gravacao_reversa) {
        alert.error("Impressão Legacy não é compatível com gravação reversa.");
        finalizarImpressao(false);
        return;
      }

      let txtLegacy = "";
      if (!removeuLogo.current && logo.length) {
        txtLegacy += `${logo}\n\n`;
        removeuLogo.current = true;
      }
      for (let i = 0; i < fila.length; i++) {
        const zplNormalizadoLegacy = normalizarDownloadsGraficos(
          fila[i].zpl_impressao,
        );
        let zplSemLogoLegacy = zplNormalizadoLegacy.split("^XA")[1] || "";
        zplSemLogoLegacy = zplSemLogoLegacy.replace(/\^XZ/g, "").trim();
        let zplLimpoLegacy = ("^XA" + (zplNormalizadoLegacy.split("^XA")[1] || ""))
          .replace(/\^XA/g, "")
          .replace(/\^XZ/g, "");
        txtLegacy += `^XA\n`;
        if (i == 0 && impressaoConfig?.comando_zpl_inicial) {
          txtLegacy += `${impressaoConfig.comando_zpl_inicial}\n`;
        }
        if (
          i > 0 &&
          i < fila.length - 1 &&
          impressaoConfig?.comando_zpl_intermediario
        ) {
          txtLegacy += `${impressaoConfig.comando_zpl_intermediario}\n`;
        }
        if (i == fila.length - 1 && impressaoConfig?.comando_zpl_final) {
          txtLegacy += `${impressaoConfig.comando_zpl_final}\n`;
        }
        txtLegacy += `${zplLimpoLegacy}\n`;
        txtLegacy += `^XZ\n\n`;
        indiceAtual.current++;
        tagsImpressasNum.current++;
      }
      downloadImpressaoLegacy(txtLegacy, impressaoConfig.app_id);
      handleAtivarCardConfirmarImpressao();
      return;
    }

    enviarMensagem("FL"); // Finalizar leitura de calibração se necessário
    enviarMensagem(`P:${impressaoConfig?.potencia_padrao || "2"}`); // Configurar potência antes de enviar pacotes

    const pacotes = dividirZplEmPacotes(zplLimpo);
    // O ~DG nao pode ser cortado no meio de uma linha: ver
    // dividirDownloadGraficoEmPacotes.
    const pacotes_logo = dividirDownloadGraficoEmPacotes(logo);

    if (!removeuLogo.current) {
      removeuLogo.current = true;
      if (logo.length)
        pacotes_logo.forEach((p) =>
          enviarMensagem(formarPacoteParaEnvio(p, false, item.tag)),
        );
    }

    if (impressaoConfig?.gravacao_reversa) {
      enviarMensagem(formarPacoteParaEnvio("^XA"));
      if (indiceAtual.current === 0 && impressaoConfig.comando_zpl_inicial) {
        enviarMensagem(
          formarPacoteParaEnvio(impressaoConfig.comando_zpl_inicial),
        );
      }
      if (
        indiceAtual.current > 0 &&
        indiceAtual.current < fila.length - 1 &&
        impressaoConfig.comando_zpl_intermediario
      ) {
        enviarMensagem(
          formarPacoteParaEnvio(impressaoConfig.comando_zpl_intermediario),
        );
      }
      if (
        indiceAtual.current === fila.length - 1 &&
        impressaoConfig.comando_zpl_final
      ) {
        enviarMensagem(
          formarPacoteParaEnvio(impressaoConfig.comando_zpl_final),
        );
      }
      pacotes.forEach((p) => enviarMensagem(formarPacoteParaEnvio(p)));
      enviarMensagem(formarPacoteParaEnvio("^XZ"));
      return;
    }
    enviarMensagem(formarPacoteParaEnvio("^XA", false, item.tag));
    if (indiceAtual.current === 0 && impressaoConfig?.comando_zpl_inicial) {
      enviarMensagem(
        formarPacoteParaEnvio(
          impressaoConfig?.comando_zpl_inicial,
          false,
          item.tag,
        ),
      );
    }
    if (
      indiceAtual.current > 0 &&
      indiceAtual.current < fila.length - 1 &&
      impressaoConfig?.comando_zpl_intermediario
    ) {
      enviarMensagem(
        formarPacoteParaEnvio(
          impressaoConfig?.comando_zpl_intermediario,
          false,
          item.tag,
        ),
      );
    }
    if (
      indiceAtual.current === fila.length - 1 &&
      impressaoConfig?.comando_zpl_final
    ) {
      enviarMensagem(
        formarPacoteParaEnvio(
          impressaoConfig?.comando_zpl_final,
          false,
          item.tag,
        ),
      );
    }
    pacotes.forEach((p) =>
      enviarMensagem(formarPacoteParaEnvio(p, false, item.tag)),
    );
    enviarMensagem(formarPacoteParaEnvio("^XZ", false, item.tag));
  };

  const dividirZplEmPacotes = (zpl: string): string[] => {
    const partes: string[] = [];
    for (let i = 0; i < zpl.length; i += 500) {
      partes.push(zpl.slice(i, i + 500));
    }
    return partes;
  };

  const salvarNaBase = async (itens: ImpressaoItem[], erroMsg: string = "") => {
    setIsLoading(true);
    try {
      if (itens.length) {
        // O histórico segue sendo atualizado etiqueta a etiqueta nos dois
        // modos; o que muda é só o momento da escrita na base.
        if (impressaoConfig?.salvar_itens_no_final) {
          itensPendentesRef.current.push(...itens);
        } else {
          await salvarItensNaBase(itens);
        }
      }
      try {
        if (historicoCriado) {
          let newErros: string[] = [...erros];
          if (erroMsg.length) {
            newErros.push(erroMsg);
            setErros((prev) => [...prev, erroMsg]);
          }

          const updatedHistorico: historicoItemResponse = {
            ...historicoCriado,
            erros: newErros,
          };
          if (!updatedHistorico.ordem_itens_tratados) {
            updatedHistorico.ordem_itens_tratados = [];
          }
          if (!erroMsg.length) {
            let indexItem = updatedHistorico.ordem_itens_tratados.findIndex(
              (itemTratado) => itemTratado._id === itens[0]._id,
            );
            if (indexItem !== -1) {
              // Atualiza o EPC se já existe
              updatedHistorico.ordem_itens_tratados[indexItem] = {
                ...updatedHistorico.ordem_itens_tratados[indexItem],
                epc: itens[0].tag || "",
              };
            } else {
              // Adiciona o item se não existe, preenchendo campos obrigatórios
              updatedHistorico.ordem_itens_tratados.push({
                _id: itens[0]._id ?? "",
                epc: itens[0].tag || "",
                localizacao_1: itens[0].id_localizacao1 || "",
                localizacao_2: itens[0].id_localizacao2 || "",
                localizacao_3: itens[0].id_localizacao3 || "",
                localizacao_4: itens[0].id_localizacao4 || "",
                valor: itens[0].valor ?? 0,
                quantidade: itens[0].quantidade ?? "",
                dt_validade: itens[0].dt_validade ?? "",
                codigo: itens[0].codigo ?? "",
                descricao: itens[0].descricao ?? "",
                inf_compl_1: itens[0].inf_compl_1 ?? "",
                inf_compl_2: itens[0].inf_compl_2 ?? "",
                inf_compl_3: itens[0].inf_compl_3 ?? "",
                inf_compl_4: itens[0].inf_compl_4 ?? "",
                inf_compl_5: itens[0].inf_compl_5 ?? "",
                inf_compl_6: itens[0].inf_compl_6 ?? "",
                inf_compl_7: itens[0].inf_compl_7 ?? "",
                inf_compl_8: itens[0].inf_compl_8 ?? "",
                inf_compl_9: itens[0].inf_compl_9 ?? "",
                inf_compl_10: itens[0].inf_compl_10 ?? "",
                inf_compl_11: itens[0].inf_compl_11 ?? "",
                inf_compl_12: itens[0].inf_compl_12 ?? "",
                id_externo: (itens[0] as any).id_externo ?? "",
                zpl: (itens[0] as any).zpl ?? "",
              });
            }
          }
          setHistoricoCriado(updatedHistorico);
          await atualizarHistorico(updatedHistorico);
        }
      } catch (error) {
        console.error("Erro ao atualizar histórico:", error);
      }
    } catch (error) {
      console.error("Erro ao salvar item na base:", error);

      alert.error(`Erro ao salvar etiquetas.`);
    } finally {
      setIsLoading(false);
      setCardConfirmarImpressao(false);
    }
  };

  /** Joga fora o lote pendente. Usado por quem já salva o recorte definitivo. */
  const descartarSalvamentoPendente = () => {
    itensPendentesRef.current = [];
  };

  /** Grava o lote que ficou pendente no modo "salvar no final". */
  const descarregarSalvamentoPendente = async () => {
    const pendentes = itensPendentesRef.current;
    if (!pendentes.length) return;
    itensPendentesRef.current = [];
    try {
      await salvarItensNaBase(pendentes);
    } catch (error) {
      console.error("Erro ao salvar o lote de etiquetas na base:", error);
      alert.error("Erro ao salvar etiquetas.");
    }
  };

  const finalizarImpressao = async (sucesso: boolean = true) => {
    // Rede de segurança: as etiquetas já saíram da impressora, então o lote
    // pendente tem que ir para a base mesmo quando a impressão termina por um
    // caminho que não passou pelo card de confirmação. Cancelar aqui também
    // mantém os itens, que é como o fluxo já se comportava.
    await descarregarSalvamentoPendente();
    setFila([]);
    indiceAtual.current = 0;
    ultimoIndiceEnviado.current = -1;
    tentativas.current = 0;
    tagsImpressasNum.current = 0;
    removeuLogo.current = false;
    blallEnviado.current = false;
    blallExecutado.current = false;
    clrqEnviado.current = false;
    if (blallTimeoutId.current) {
      clearTimeout(blallTimeoutId.current);
      blallTimeoutId.current = null;
    }
    setImpressaoPausada(false);
    setCardConfirmarImpressao(false);
    setEstaImprimindo(false);
    setDeuErro(false);
    setCriouHistorico(false);
    setCardAlterarItens(false);
    setCodigoDigitado(""); // Limpa o código digitado
    if (sucesso) {
      alert.success("Impressão finalizada com sucesso!");
    }
  };

  useEffect(() => {
    return () => {
      finalizarImpressao();
    };
  }, []);

  return (
    <ImpressaoPanvelContext.Provider
      value={{
        adicionarItensNaFila,
        fila,
        estaImprimindo,
        tagsImpressasNum,
        finalizarImpressao,
        descartarSalvamentoPendente,
        deuErro,
        erroMensagem,
        cardConfirmarImpressao,
        setCardConfirmarImpressao,
        handleAlterarItens,
        cardAlterarItens,
        setFila,
        setItensParaRemocao,
        itensParaRemocao,
        codigoDigitado,
        travarSeisDígitos,
        setTravarSeisDígitos,
      }}
    >
      <audio ref={erroAudioRef} src={ERRO_AUDIO_PATH} preload="auto" />
      {children}
      <ImpressaoPanvel erroVisual={erroVisual} />
    </ImpressaoPanvelContext.Provider>
  );
}

type EtiquetaImpressaoData = {
  epc: string;
  estado: string;
};

export function useImpressaoPanvel() {
  const context = useContext(ImpressaoPanvelContext);
  if (!context) {
    throw new Error(
      "useImpressaoPanvel deve ser usado dentro de ImpressaoProvider",
    );
  }
  return context;
}

// ---- Estado badge helper (compartilhado visualmente com ImpressaoComponent) ----
type ImpressaoEstado = "impresso" | "imprimindo" | "erro" | "aguardando";

function EstadoBadge({
  estado,
  erroMensagem,
}: {
  estado: ImpressaoEstado;
  erroMensagem: string;
}) {
  const variants: Record<
    ImpressaoEstado,
    { cls: string; icon: any; label: string }
  > = {
    impresso: {
      cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
      icon: Check,
      label: "Impresso",
    },
    imprimindo: {
      cls: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
      icon: Loader2,
      label: "Enviando",
    },
    erro: {
      cls: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
      icon: AlertTriangle,
      label: erroMensagem || "Erro",
    },
    aguardando: {
      cls: "bg-muted text-muted-foreground border-border",
      icon: Clock,
      label: "Aguardando",
    },
  };
  const v = variants[estado];
  const Icon = v.icon;
  return (
    <Badge variant="outline" className={`${v.cls} gap-1`}>
      <Icon
        className={`h-3 w-3 ${estado === "imprimindo" ? "animate-spin" : ""}`}
      />
      <span className="truncate max-w-[200px]">{v.label}</span>
    </Badge>
  );
}

function StatBox({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent: "emerald" | "amber" | "muted";
}) {
  const accentMap = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    muted: "text-foreground",
  };
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <div className={`text-2xl font-bold leading-tight ${accentMap[accent]}`}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
        {label}
      </div>
    </div>
  );
}

function ImpressaoPanvel({ erroVisual = false }: { erroVisual?: boolean }) {
  const context = useImpressaoPanvel();

  const {
    fila,
    estaImprimindo,
    tagsImpressasNum,
    finalizarImpressao,
    descartarSalvamentoPendente,
    deuErro,
    erroMensagem,
    cardConfirmarImpressao,
    setCardConfirmarImpressao,
    handleAlterarItens,
    cardAlterarItens,
    setFila,
    itensParaRemocao,
    setItensParaRemocao,
    codigoDigitado,
    travarSeisDígitos,
    setTravarSeisDígitos,
  } = context;
  const { salvarItensNaBase, apagarItensNaBase } = useItem();
  const { isLoading } = useGlobalLoader();

  const columns: ColumnDef<EtiquetaImpressaoData>[] = [
    {
      accessorKey: "epc",
      header: "EPC",
      cell: ({ row }) => (
        <span className="text-xs">{row.getValue("epc")}</span>
      ),
    },
    {
      accessorKey: "estado",
      header: "Estado",
      cell: ({ row }) => (
        <EstadoBadge
          estado={row.getValue("estado") as ImpressaoEstado}
          erroMensagem={erroMensagem}
        />
      ),
    },
  ];

  const columnsApagarItens: ColumnDef<ImpressaoItem>[] = [
    {
      accessorKey: "tag",
      header: "EPC",
      cell: ({ row }) => (
        <span className="text-xs">{row.getValue("tag")}</span>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Ações</div>,
      cell: ({ row }) => {
        const epc = row.getValue("tag") as string;
        const naFila = fila.some((item) => item.tag === epc);
        return (
          <div className="text-right">
            <Button
              variant={naFila ? "destructive" : "outline"}
              size="sm"
              onClick={() => {
                if (!naFila) {
                  const item = itensParaRemocao.find((i) => i.tag === epc);
                  if (item) {
                    apagarItensNaBase([item]);
                    setItensParaRemocao((prev) =>
                      prev.filter((i) => i.tag !== epc),
                    );
                    setFila((prev) => [...prev, item]);
                  }
                } else {
                  const itemEncontrado = fila.find((i) => i.tag === epc);
                  if (itemEncontrado) {
                    apagarItensNaBase([itemEncontrado]);
                  }
                  setFila((prev) => prev.filter((i) => i.tag !== epc));
                  setItensParaRemocao((prev) =>
                    prev.filter((i) => i.tag !== epc),
                  );
                  // O item removido é um impresso (a tabela só lista impressos).
                  // Decrementa para manter slice(0, tagsImpressasNum) apontando
                  // só para os impressos restantes — senão um item não impresso
                  // entraria no recorte e seria salvo indevidamente.
                  tagsImpressasNum.current = Math.max(
                    0,
                    tagsImpressasNum.current - 1,
                  );
                }
                alert.success(`Etiqueta ${epc} removida da base.`);
              }}
            >
              {naFila ? (
                <>
                  <Trash2 className="h-3.5 w-3.5" />
                  Remover da base
                </>
              ) : (
                <>
                  <RotateCw className="h-3.5 w-3.5" />
                  Restaurar
                </>
              )}
            </Button>
          </div>
        );
      },
    },
  ];

  if (!estaImprimindo) return null;

  const total = fila.length;
  const impressas = tagsImpressasNum.current;
  const restante = Math.max(0, total - impressas);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm transition-colors duration-200 ${
        erroVisual ? "bg-red-700/70 animate-pulse" : "bg-background/80"
      }`}
    >
      <Card
        className={`w-full max-w-3xl shadow-xl flex flex-col max-h-[92vh] overflow-hidden transition-colors duration-200 ${
          erroVisual ? "border-4 border-red-600" : ""
        }`}
      >
        <CardHeader className="border-b">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full shrink-0 ${
                  deuErro ? "bg-red-500/10" : "bg-primary/10"
                }`}
              >
                <Printer
                  className={`h-5 w-5 ${
                    deuErro ? "text-red-500" : "text-primary"
                  }`}
                />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-base">
                  Impressão por Bipagem
                </CardTitle>
                <CardDescription className="text-xs">
                  Leia o código de barras ou digite manualmente para imprimir.
                </CardDescription>
              </div>
            </div>
            <Badge
              variant="outline"
              className={
                deuErro
                  ? "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20 gap-1"
                  : codigoDigitado
                    ? "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20 gap-1"
                    : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 gap-1"
              }
            >
              {deuErro ? (
                <>
                  <AlertTriangle className="h-3 w-3" />
                  Erro
                </>
              ) : codigoDigitado ? (
                <>
                  <Keyboard className="h-3 w-3" />
                  Digitando
                </>
              ) : (
                <>
                  <Clock className="h-3 w-3" />
                  Aguardando digitação
                </>
              )}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="flex flex-col gap-4 overflow-auto py-4">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-2">
            <StatBox label="Impressas" value={impressas} accent="emerald" />
            <StatBox label="Restantes" value={restante} accent="amber" />
          </div>

          {/* Código digitado em destaque */}
          <div className="rounded-md border bg-muted/20 p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Keyboard className="h-3 w-3" />
                Código digitado
              </span>
              {codigoDigitado && (
                <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium animate-pulse">
                  Pressione Enter pra processar
                </span>
              )}
            </div>
            <div
              className={`text-2xl text-center py-2 rounded border transition-colors ${
                codigoDigitado
                  ? "border-blue-500/40 bg-blue-500/5 text-foreground"
                  : "border-dashed border-border text-muted-foreground/50"
              }`}
            >
              {codigoDigitado || "—"}
            </div>
          </div>

          {/* Opção 6 dígitos */}
          <label
            htmlFor="travar-seis-digitos"
            className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 cursor-pointer text-sm"
          >
            <Checkbox
              id="travar-seis-digitos"
              checked={travarSeisDígitos}
              onCheckedChange={(checked) => setTravarSeisDígitos(!!checked)}
            />
            <span className="text-xs">Obrigar código de 6 dígitos</span>
          </label>

          {/* Tabela */}
          <div className="rounded-md border overflow-hidden">
            <DataTable
              autoResetPageIndex={false}
              columns={columns}
              data={(() => {
                const itensNaoImpressos = fila
                  .slice(tagsImpressasNum.current)
                  .map((item, index) => ({
                    epc: item.tag,
                    estado:
                      index === 0
                        ? deuErro
                          ? "erro"
                          : "imprimindo"
                        : "aguardando",
                  }));
                const itensImpressos = fila
                  .slice(0, tagsImpressasNum.current)
                  .map((item) => ({
                    epc: item.tag,
                    estado: "impresso" as const,
                  }));
                return [...itensNaoImpressos, ...itensImpressos];
              })()}
            />
          </div>
        </CardContent>

        <CardFooter className="border-t pt-4 justify-end">
          <Button
            disabled={isLoading}
            variant="destructive"
            size="sm"
            onClick={() => setCardConfirmarImpressao(true)}
          >
            <X className="h-4 w-4" />
            Finalizar impressão
          </Button>
        </CardFooter>
      </Card>

      {/* Sub-dialog: confirmar finalização */}
      {cardConfirmarImpressao && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/70 backdrop-blur-sm p-4">
          <Card className="w-full max-w-md shadow-xl">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <Check className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">
                    Suas etiquetas foram impressas corretamente?
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Confirme pra salvar na base. Se houve problema, use
                    "Alterar itens".
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardFooter className="flex flex-wrap gap-2 justify-end">
              <Button
                disabled={isLoading}
                variant="destructive"
                size="sm"
                onClick={() => finalizarImpressao(false)}
              >
                <X className="h-4 w-4" />
                Cancelar
              </Button>
              <Button
                disabled={isLoading}
                variant="outline"
                size="sm"
                onClick={() => handleAlterarItens()}
              >
                <Edit3 className="h-4 w-4" />
                Alterar itens
              </Button>
              <Button
                disabled={isLoading}
                size="sm"
                onClick={async () => {
                  setCardConfirmarImpressao(false);
                  const itensImpressos = fila.slice(
                    0,
                    tagsImpressasNum.current,
                  );
                  // Este recorte já é a lista definitiva do que vai para a
                  // base; descartar o lote pendente evita que um item removido
                  // em "Alterar itens" volte pelo flush de finalizarImpressao.
                  descartarSalvamentoPendente();
                  await salvarItensNaBase(itensImpressos);
                  finalizarImpressao(true);
                }}
              >
                <Check className="h-4 w-4" />
                Confirmar
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Sub-dialog: alterar itens */}
      {cardAlterarItens && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/70 backdrop-blur-sm p-4">
          <Card className="w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">
            <CardHeader className="border-b">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="text-base">
                    Apague os itens com defeito
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Marque pra remover da base as etiquetas que não saíram
                    corretamente.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-auto py-4">
              <div className="rounded-md border overflow-hidden">
                <DataTable
                  columns={columnsApagarItens}
                  data={fila.slice(0, tagsImpressasNum.current)}
                />
              </div>
            </CardContent>
            <CardFooter className="border-t pt-4 justify-end">
              <Button
                disabled={isLoading}
                size="sm"
                onClick={async () => {
                  setCardConfirmarImpressao(false);
                  descartarSalvamentoPendente();
                  await salvarItensNaBase(
                    fila.slice(0, tagsImpressasNum.current),
                  );
                  finalizarImpressao(true);
                }}
              >
                <Check className="h-4 w-4" />
                Confirmar alterações
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
}
